// Service for managing search history in localStorage
export interface SearchHistoryItem {
  query: string;
  timestamp: number;
  type?: 'manual' | 'suggestion';
}

class SearchHistoryService {
  private readonly STORAGE_KEY = 'sap_search_history';
  private readonly MAX_HISTORY_ITEMS = 10;

  /**
   * Get search history from localStorage
   */
  getSearchHistory(): SearchHistoryItem[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const history: SearchHistoryItem[] = JSON.parse(stored);
      
      // Filter out entries older than 30 days and sort by timestamp
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      return history
        .filter(item => item.timestamp > thirtyDaysAgo)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, this.MAX_HISTORY_ITEMS);
    } catch (error) {
      console.warn('Failed to load search history:', error);
      return [];
    }
  }

  /**
   * Add a search query to history
   */
  addToHistory(query: string, type: 'manual' | 'suggestion' = 'manual'): void {
    if (!query.trim()) return;

    try {
      const history = this.getSearchHistory();
      const normalizedQuery = query.trim().toLowerCase();
      
      // Remove existing entry with same query (case-insensitive)
      const filteredHistory = history.filter(
        item => item.query.toLowerCase() !== normalizedQuery
      );
      
      // Add new entry at the beginning
      const newItem: SearchHistoryItem = {
        query: query.trim(),
        timestamp: Date.now(),
        type
      };
      
      const updatedHistory = [newItem, ...filteredHistory].slice(0, this.MAX_HISTORY_ITEMS);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedHistory));
    } catch (error) {
      console.warn('Failed to save search history:', error);
    }
  }

  /**
   * Clear all search history
   */
  clearHistory(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear search history:', error);
    }
  }

  /**
   * Remove a specific item from history
   */
  removeFromHistory(query: string): void {
    try {
      const history = this.getSearchHistory();
      const filteredHistory = history.filter(item => item.query !== query);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredHistory));
    } catch (error) {
      console.warn('Failed to remove from search history:', error);
    }
  }
}

export const searchHistoryService = new SearchHistoryService();
